import { useCallback, useEffect, useRef, useState } from "react";
import Lottie from "lottie-react";
import { TOMATO_THROW_JSON } from "../reactionThrowAssets";

const GIF_REACTION_MS = 4200;

/** Wall-clock time to show `The Morty Dance Loader.json` while it loops (~0.45s native length). */
const MORTY_DANCE_HOLD_MS = 3000;

function isGifMediaUrl(url) {
  return typeof url === "string" && /\.gif(\?|#|$)/i.test(url);
}

function reactionLottieDecodedPath(url) {
  if (typeof url !== "string") return "";
  try {
    const base =
      typeof window !== "undefined" && window.location
        ? window.location.href
        : "https://local.invalid/";
    return decodeURIComponent(new URL(url, base).pathname);
  } catch {
    return url;
  }
}

/** Match encoded or decoded URL path (CloudFront / CRA public paths). */
function isMortyDanceLoaderSrc(url) {
  if (typeof url !== "string") return false;
  const p = reactionLottieDecodedPath(url);
  return (
    /Morty Dance Loader\.json$/i.test(p) ||
    /Morty%20Dance%20Loader\.json/i.test(url) ||
    url.includes("Morty%20Dance%20Loader")
  );
}

/** SVG Lottie is flaky on some iOS Safari builds; canvas is more reliable. */
function useCanvasRendererForReactionLottie() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  return iOS;
}

function deferToNextFrames(fn) {
  if (typeof window === "undefined") {
    fn();
    return;
  }
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(fn);
  });
}

function GifSplatPlayer({ src, filter, className, onComplete }) {
  useEffect(() => {
    const t = window.setTimeout(onComplete, GIF_REACTION_MS);
    return () => window.clearTimeout(t);
  }, [src, onComplete]);

  const outerStyle = {
    width: "100%",
    height: "100%",
    minHeight: "100%",
    lineHeight: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    ...(filter && filter !== "none" ? { filter } : { filter: "none" }),
  };

  return (
    <div className={className} style={outerStyle} aria-hidden>
      <img
        src={src}
        alt=""
        decoding="async"
        className="reaction-splat-gif-img"
        style={{
          maxWidth: "100%",
          maxHeight: "100%",
          width: "auto",
          height: "auto",
          objectFit: "contain",
        }}
      />
    </div>
  );
}

/**
 * Bodymovin JSON via lottie-web (reliable colours + visibility for AE exports).
 *
 * @param {string} src - URL to JSON under `public/`
 * @param {string} [filter] - CSS filter on wrapper (tomato tint); omit for native colours
 */
function LottieSplatPlayerJson({
  filter,
  onComplete,
  className,
  src,
}) {
  const mortyHold = isMortyDanceLoaderSrc(src);
  const canvasRenderer = useCanvasRendererForReactionLottie();
  const [animationData, setAnimationData] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const finished = useRef(false);
  const fallbackTimerRef = useRef(null);
  const lottieRef = useRef(null);

  const finishOnce = useCallback(() => {
    if (finished.current) return;
    finished.current = true;
    if (fallbackTimerRef.current != null) {
      window.clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
    onComplete();
  }, [onComplete]);

  useEffect(() => {
    finished.current = false;
    setAnimationData(null);
    setLoadFailed(false);
    let cancelled = false;

    fetch(src, { credentials: "same-origin" })
      .then((res) => {
        if (!res.ok) throw new Error(String(res.status));
        return res.json();
      })
      .then((json) => {
        if (!cancelled) setAnimationData(json);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [src]);

  useEffect(() => {
    if (!loadFailed) return undefined;
    const t = window.setTimeout(finishOnce, 150);
    return () => window.clearTimeout(t);
  }, [loadFailed, finishOnce]);

  useEffect(() => {
    if (!animationData) return undefined;
    if (mortyHold) {
      fallbackTimerRef.current = window.setTimeout(finishOnce, MORTY_DANCE_HOLD_MS + 300);
    } else {
      const fr = typeof animationData.fr === "number" ? animationData.fr : 30;
      const op = animationData.op != null ? Number(animationData.op) : 0;
      const ip = animationData.ip != null ? Number(animationData.ip) : 0;
      const sec = Math.max(0, (op - ip) / fr);
      // Generous tail so trimmed comps (e.g. kiss/mouth.json ip→op) are not cut off if `complete` never fires.
      const ms = Math.min(60000, Math.max(4000, sec * 1000 + 3500));
      fallbackTimerRef.current = window.setTimeout(finishOnce, ms);
    }
    return () => {
      if (fallbackTimerRef.current != null) {
        window.clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
    };
  }, [animationData, mortyHold, finishOnce]);

  const handleDOMLoaded = useCallback(() => {
    const data = animationData;
    if (!data) return;
    const ip = Math.floor(Number(data.ip ?? 0));
    const op = Math.ceil(Number(data.op ?? 0));
    deferToNextFrames(() => {
      const ctrl = lottieRef.current;
      if (!ctrl) return;
      if (mortyHold) {
        ctrl.play();
        return;
      }
      if (Number.isFinite(ip) && Number.isFinite(op) && op > ip) {
        ctrl.playSegments([ip, op], true);
      } else {
        ctrl.play();
      }
    });
  }, [animationData, mortyHold]);

  const outerStyle = {
    width: "100%",
    height: "100%",
    minHeight: "100%",
    lineHeight: 0,
    ...(filter && filter !== "none" ? { filter } : { filter: "none" }),
  };

  if (!animationData) {
    return <div className={className} style={outerStyle} aria-hidden />;
  }

  return (
    <div className={className} style={outerStyle}>
      <Lottie
        lottieRef={lottieRef}
        animationData={animationData}
        loop={mortyHold}
        autoplay={false}
        onDOMLoaded={handleDOMLoaded}
        onComplete={mortyHold ? null : finishOnce}
        renderer={canvasRenderer ? "canvas" : "svg"}
        style={{ width: "100%", height: "100%" }}
        rendererSettings={{
          preserveAspectRatio: "xMidYMid meet",
        }}
      />
    </div>
  );
}

/**
 * Bodymovin JSON via lottie-web, or a `.gif` URL (fixed-duration splash).
 *
 * @param {string} [src] - URL to JSON or GIF under `public/`
 * @param {string} [filter] - CSS filter on wrapper (tomato tint); omit for native colours
 */
export function LottieSplatPlayer({
  filter,
  onComplete,
  className,
  src = TOMATO_THROW_JSON,
}) {
  if (isGifMediaUrl(src)) {
    return (
      <GifSplatPlayer
        src={src}
        filter={filter}
        className={className}
        onComplete={onComplete}
      />
    );
  }
  return (
    <LottieSplatPlayerJson
      filter={filter}
      onComplete={onComplete}
      className={className}
      src={src}
    />
  );
}
